import { generateFileUrlForLogicalName } from '../../qti/parser/question/MediaModels';
import { parseAssessmentItem } from '../../qti/parser/question/QTIAssessmentItem';
import settingsReader from '../../store/readHelpers/SettingsReader';
import mediaSupportService from '../../services/MediaSupportService';
import constants from '../../qti/Constants';
import marketplacePropertiesService from '../../services/MarketplacePropertiesService';

const mediaFinder = {
    getAllQuestionsAndDocumentsFromQuestionSet(questionSet) {
        const qsClone = JSON.parse(JSON.stringify(questionSet));
        return qsClone
            .sections
            .map((section) => {
                if (section.stimulusContent) {
                    section.questions.push(section.stimulusContent);
                }
                return section;
            })
            .reduce((accumulator, section) => {
                accumulator.push(...section.questions);
                return accumulator;
            }, []);
    },

    getMediaModelsInQuestionSet(questionSet, additionalResourceTypes = []) {
        const { assessmentRunId } = questionSet;
        const mediaModels = this.getMediaModelsForAdditionalResources(questionSet, assessmentRunId, additionalResourceTypes);
        return mediaFinder
            .getAllQuestionsAndDocumentsFromQuestionSet(questionSet)
            .reduce((accumulator, question) => {
                accumulator.push(...this.getMediaModelsInQuestion(question, assessmentRunId));
                return accumulator;
            }, mediaModels);
    },

    getMediaModelsInQuestion(question, assessmentRunId) {
        try {
            const questionModel = question.isParsed ? question : parseAssessmentItem(question, question.defaultLanguage, assessmentRunId);
            const interactionFiles = mediaFinder.getMediaModelsInQuestionInteractions(questionModel, assessmentRunId);
            return [...questionModel.mediaModels, ...interactionFiles];
        } catch (error) {
            console.error(error);
            return [];
        }
    },

    getMediaModelsInQuestionInteractions(question, assessmentRunId) {
        if (question.responseModels === undefined) {
            return [];
        }
        const mediaModels = question.responseModels
            .reduce((accumulator, responseModel) => {
                if (responseModel.type in mediaFinder) {
                    accumulator.push(
                        ...mediaFinder[responseModel.type](responseModel, assessmentRunId)
                    );
                }
                return accumulator;
            }, []);

        return mediaModels;
    },

    getMediaModelsForAdditionalResources(questionSet, assessmentRunId, types) {
        if (!Array.isArray(questionSet?.additionalResources)) {
            return [];
        }

        return questionSet.additionalResources
            .filter(resource => types?.includes(resource.type))
            .map(({ logicalName, type, mediaInfo }) => {
                if (type === constants.additionalResourceType.AUDIO) {
                    return this.getMediaModelForBackgroundAudio(questionSet);
                }
                const servletUrl = generateFileUrlForLogicalName(logicalName, undefined, assessmentRunId, type);
                const url = settingsReader.shouldUsePresignedUrl() && mediaInfo?.presignedURL
                    ? mediaInfo?.presignedURL
                    : servletUrl;
                return {
                    url,
                    type,
                };
            });
    },

    hasOpusSupport(resource) {
        const resourceSupportsOpus = (
            resource.mediaInfo
            && Array.isArray(resource.mediaInfo.formats)
            && resource.mediaInfo.formats.includes('opus')
        );

        return resourceSupportsOpus && mediaSupportService.browserSupportsAudioFormat('opus');
    },

    getMediaModelForBackgroundAudio(questionSet) {
        const assessmentRunId = questionSet.assessmentRunId;
        const audioResource = questionSet.additionalResources?.find(resource => resource.type === constants.additionalResourceType.AUDIO);
        if (!audioResource) {
            return null;
        }

        const { logicalName, type, mediaInfo, partStartTimes } = audioResource;
        const hasOpusSupport = this.hasOpusSupport(audioResource);
        const sources = [{ url: generateFileUrlForLogicalName(logicalName, undefined, assessmentRunId, type) }];
        if (settingsReader.shouldUsePresignedUrl() && mediaInfo?.presignedURL) {
            sources.unshift({ url: mediaInfo.presignedURL });
        }
        if (hasOpusSupport) {
            sources.unshift({ url: generateFileUrlForLogicalName(logicalName, undefined, assessmentRunId, type, 'opus') });
            if (settingsReader.shouldUsePresignedUrl() && Array.isArray(mediaInfo.formatsInfo)) {
                const opusFormat = mediaInfo.formatsInfo.find(formatInfoObj => formatInfoObj.format === 'opus');
                if (opusFormat) {
                    sources.unshift({ url: opusFormat.presignedURL });
                }
            }
        }
        return {
            logicalName,
            sources,
            type,
            partStartTimes,
        };
    },

    getMediaModelsForPDFPanels(question) {
        return question.PDFPanelModel ? [question.PDFPanelModel] : [];
    },

    getPDFPanelModelsInQuestionSet(questionSet) {
        const { assessmentRunId } = questionSet;
        const result = mediaFinder
            .getAllQuestionsAndDocumentsFromQuestionSet(questionSet)
            .reduce((accumulator, question) => {
                try {
                    const questionModel = question.isParsed ? question : parseAssessmentItem(question, question.defaultLanguage, assessmentRunId);
                    const pdfModels = this.getMediaModelsForPDFPanels(questionModel);
                    accumulator.push(...pdfModels);
                } catch (error) {
                    const questionIdentifier = question && (question.identifier || question.id || question.qtiIdentifier);
                    console.error(
                        'Failed to parse assessment item while collecting PDF panel models.', { error, assessmentRunId, questionIdentifier }
                    );
                }
                return accumulator;
            }, []);
        return result;
    },

    getImageModels(responseModel) {
        return responseModel.mediaModels || [];
    },

    choiceInteraction(responseModel) {
        if (responseModel.hasAudioAlternatives) {
            return responseModel.alternatives
                .filter(alternative => Array.isArray(alternative.mediaModels) && alternative.mediaModels.length > 0)
                .map(alternative => alternative.mediaModels[0]);
        }

        return responseModel.alternatives
            .reduce((accumulator, alternative) => {
                if (Array.isArray(alternative.mediaModels)) {
                    accumulator.push(...alternative.mediaModels);
                }
                return accumulator;
            }, []);
    },

    gapMatchInteraction(responseModel) {
        const interactionBodyOutput = this.getImageModels(responseModel);
        const graphicGapMatchOutput = this.graphicGapMatchInteraction(responseModel);
        return [...interactionBodyOutput, ...graphicGapMatchOutput];
    },

    graphicGapMatchInteraction(responseModel) {
        const imageModels = [];
        if (typeof responseModel.backgroundImageModel === 'object') {
            imageModels.push(responseModel.backgroundImageModel);
        }

        const gapChoiceImageModels = responseModel.gapChoiceModels
            .filter(gapChoiceModel => gapChoiceModel.type === 'gapImg' && typeof gapChoiceModel.mediaModel === 'object')
            .map(gapChoiceModel => gapChoiceModel.mediaModel);

        imageModels.push(...gapChoiceImageModels);
        return imageModels;
    },

    hotspotInteraction(responseModel) {
        if (typeof responseModel.backgroundImageModel === 'object') {
            return [responseModel.backgroundImageModel];
        }
        return [];
    },

    matchInteraction(responseModel, assessmentRunId) {
        const isImagesInOfflineModeEnabled =
            marketplacePropertiesService.isImagesLoadInMatchingPairingOfflineModeEnabled();

        if (!isImagesInOfflineModeEnabled) {
            return [];
        }

        const {
            contentAttributes,
            urlPrefixes,
            additionalResourceType,
            queryParams,
            DEFAULT_MATCH_IMAGE_NAME,
            DEFAULT_MATCH_MIME_TYPE,
        } = constants;

        const objects = Array.isArray(responseModel && responseModel.objects)
            ? responseModel.objects
            : [];

        const filteredObjects = objects.filter((mediaObject) => {
            return (
                mediaObject &&
                mediaObject.data &&
                mediaObject[contentAttributes.OBJECT_TYPE] ===
                additionalResourceType.CONTENT_IMAGE
            );
        });

        return filteredObjects.map((mediaObject) => {
            let finalUrl = mediaObject.data;

            const isAbsoluteUrl = urlPrefixes.HTTP_PREFIX.some((httpPrefix) => {
                return finalUrl.startsWith(httpPrefix);
            });

            if (!isAbsoluteUrl) {
                try {
                    finalUrl = new URL(
                        finalUrl,
                        window.location.origin
                    ).toString();
                } catch (error) {
                    const needsLeadingSlash =
                    finalUrl && !finalUrl.startsWith('/');
                    const prefixSeparator = needsLeadingSlash ? '/' : '';
                    finalUrl =
                    window.location.origin +
                    prefixSeparator +
                    (finalUrl || '');
                }
            }

            const effectiveAssessmentRunId =
            assessmentRunId ||
            (responseModel && responseModel.assessmentRunId) ||
            '';

            if (
                effectiveAssessmentRunId &&
                queryParams.ASSESSMENT_RUN_ID
            ) {
                const separator = finalUrl.includes('?') ? '&' : '?';
                finalUrl += `${separator}${queryParams.ASSESSMENT_RUN_ID}=${effectiveAssessmentRunId}`;
            }

            return {
                url: finalUrl,
                logicalName:
                    mediaObject[contentAttributes.LOGICAL_NAME] ||
                    DEFAULT_MATCH_IMAGE_NAME,
                type: mediaObject.type || DEFAULT_MATCH_MIME_TYPE,
            };
        });
    },

};

export default mediaFinder;
